import React from "react";
import { Button, Modal, OverlayTrigger, Tooltip } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faRotate } from "@fortawesome/pro-light-svg-icons";
import Dialog from "../../../../util/Dialog";

import ApiKeyContext from "../../../../contexts/ApiKeyContext";
import CatalogContext from "../../../../contexts/CatalogContext";
import PageContext from "../../../../contexts/PageContext";
import { useProductDetailContext } from "../../../../hooks/useProductDetailContext";

import { useAuth } from "../../../../auth/useAuth";

import { download, getBossWrapper, processImagePath, trackEvent, } from "../../../../util/Functions";

import ProductMedia from "./ProductMedia";
import ItemInquiryButton from "./item_inquiry/ItemInquiryButton";
import ProductAttributes from "./ProductAttributes";
import ProductAttribute from "./ProductAttribute";
import BookmarkItemButton from "../../components/bookmark/BookmarkItemButton";
import LoadMask from "../../../../components/LoadMask";
import ProductShareViaEmailModal from "../share_input/ProductShareViaEmailModal";
import ProductShareViaSmsModal from "../share_input/ProductShareViaSmsModal";
import ProposalItemActions from "../proposal/ProposalItemActions";


import ProductDetailMenu from "./ProductDetailMenu";


import "./ProductDetail.scss";

/** @jsxImportSource @emotion/react */
import { CanceledError } from "axios";
import { useCustomer } from "../../../../auth/useCustomer";
import { ProductUtil } from "../../../../util/Product.util";
import { DB } from "../../../../util/Cache.util";
import ScannableCodeFullscreen from "../scannable_code/ScannableCodeFullscreen";
import LiveApiClient from "../../../../api/LiveApiClient";
import { ObjectUtil } from "../../../../util/Object.util";
import { useLog } from "../../../../auth/useLog";
import { useSidebarContext } from "../../../../hooks/useSidebarContext";
import { ActionBus } from "../../../../models/ActionBus";
import ProductInvariationDialog from "../../../../components/invariation/ProductInvariationDialog";
import FormButton from "./../../components/form/FormButton";
import { ArrayUtil } from "../../../../util/Array.util";
import Catalog from "../../../../models/Catalog";

const ItemRelationships = React.lazy(() => import('./ItemRelationships'));
const RequestProductButton = React.lazy(() => import("./request_product/request_product_button/RequestProductButton"));

export const NO_PRODUCT_FOUND_AUTO_CLOSE_TIMEOUT_MILLIS = 2_000;

export default function ProductDetail() {
	const { modalProps, setModalProps, actionBus } = useProductDetailContext();
	const [attributes, setAttributes] = React.useState(false);
	const [product, setProduct] = React.useState(null);
	const { currentUser, organizationMember } = useAuth();
	const preloaded_products = React.useRef(new Map());
	const preloaded_timeouts = React.useRef(new Set());
	const no_product_modal = React.useRef(null);
	const abortController = React.useRef(new AbortController());
	const [relatedItemCount, setRelatedItemCount] = React.useState(false);
	const formsAbortController = React.useRef(new AbortController());
	const [forms, setForms] = React.useState([]);
	const SidebarContext = useSidebarContext();

	const [showQr, setShowQr] = React.useState(false);
	const [showPhoneNumberPrompt, setShowPhoneNumberPrompt] = React.useState(false);
	const [showEmailPrompt, setShowEmailPrompt] = React.useState(false);

	const [catalog] = React.useContext(CatalogContext);
	const { currentPage } = React.useContext(PageContext);

	// const { addNewChatMessage, setChatTarget } = useChatContext();
	const [loading, _setLoading] = React.useState(false);
	const loaded_item = React.useRef();
	const common_request_headers = React.useRef();

	const CustomerContext = useCustomer();
	const customerAllowed = CustomerContext.isAllowed(catalog);
	const customerPhone = CustomerContext.customer?.phone_mobile;
	const customerEmail = CustomerContext.customer?.email;

	const API_KEY_CONTEXT = React.useContext(ApiKeyContext);
	const LogContext = useLog();

	const dbName = DB.Make_Name(catalog.id);
	const identifier = React.useMemo(() => modalProps.identifier, [modalProps.identifier]);

	const empty_loading_container = (<div>
		<LoadMask show={loading} />
	</div>)

	const [loadingNextVariations, setLoadingNextVariations] = React.useState(undefined);

	const setLoading = React.useCallback(val => {
		getBossWrapper().classList.toggle("is-loading", val);
		_setLoading(val);
	}, [_setLoading]);

	const cacheKeyPrefixes = identifier ? [identifier] : ["id", "sku", "upc"];


	// log change when component parts change
	React.useEffect(() => {
		if (!modalProps.item) return;
		if (!currentPage || Object.keys(currentPage).length === 0) return;

		LogContext.logPageView({
			page: currentPage,
			action: "view",
			item: modalProps.item
		});

	}, [
		modalProps.item,
		currentPage
	]);

	React.useEffect(() => {
		common_request_headers.current = {
			headers: {
				'api-key': API_KEY_CONTEXT.apiKey,
				'cache-control': !localStorage.getItem('nocache') ? 'private' : 'no-cache',
				account: API_KEY_CONTEXT.account,
			},
			signal: abortController.current?.signal
		};
	}, [API_KEY_CONTEXT?.apiKey]);

	const getProductRequest = React.useCallback(async (propertyValue, { isItemId } = {}) => {
		if (!LiveApiClient || !propertyValue) return Promise.resolve(null);

		const preloads = await Promise.all(
			cacheKeyPrefixes.map(key =>
				ProductUtil.GetPreloadedProduct(catalog.id, [key, propertyValue])
			)
		);
		const preloaded = preloads.find(it => it);
		if (preloaded) {
			return preloaded;
		}


		let item;
		if (identifier) {
			item = await ProductUtil.GetProduct(propertyValue, {
				variations: true,
				signal: abortController.current?.signal,
				apiContext: API_KEY_CONTEXT
			});
		}
		else {
			item = await ProductUtil.FindProduct(cacheKeyPrefixes, propertyValue, {
				variations: true,
				signal: abortController.current?.signal,
				apiContext: API_KEY_CONTEXT,
			});
		}

		if (item) {
			ProductUtil.SetPreloadedProduct(catalog.id, cacheKeyPrefixes, item);

			preloaded_products.current
				.set(`id::${item.id}`, item)
				.set(`sku::${item.common_sku}`, item);
			item.upc?.trim().length && preloaded_products.current.set(`upc::${item.upc}`, item);
		}

		return item;
	}, [identifier]);

	React.useEffect(() => {
		const forms = JSON.parse(JSON.stringify(catalog.options?.forms || []));
		// Unique and not set
		const formIds = [...forms.map(f => f.form)]
			.filter(f => typeof (f) !== 'object');

		if (!formIds.length) {
			return;
		}
		LiveApiClient.get('/Catalog_Form', {
			headers: {
				account: API_KEY_CONTEXT.account,
				'api-key': API_KEY_CONTEXT.apiKey,
				'content-control': 'private'
			},
			params: {
				filters: JSON.stringify([{ property: 'id', value: formIds }]),
				paths: JSON.stringify(['*'])
			},
			signal: formsAbortController.current?.signal
		}).then(response => {
			forms.forEach(form => form.form = response.data.find(f => f.id === +form.form));
			setForms(forms);
		});
	}, []);


	const cancelPreloads = () => {
		abortController.current.abort();
		abortController.current = new AbortController();
		preloaded_timeouts.current.forEach(clearTimeout);
		preloaded_timeouts.current.clear();
	};

	const getAttributes = async () => {
		if (attributes) return attributes;
		if (!modalProps.item) return;

		const endpoint_parts = modalProps.item_attr_endpoint
			? [modalProps.item_attr_endpoint, `?item=${modalProps.item}`]
			: ['/Catalog_Page/', currentPage.id, '/attributes/details', '?item=', modalProps.item];
		const attribute_endpoint = endpoint_parts.join("");

		if (!localStorage.getItem('nocache')) {
			const cachedValue = await DB.Get(dbName, endpoint_parts);
			if (cachedValue && localStorage.getItem('nocache')) {
				console.warn(`Cached attributes found and not used; "nocache" option active`);
			}
			else if (cachedValue) {
				setAttributes(cachedValue);
				return cachedValue;
			}
		}
		const headers = {
			account: API_KEY_CONTEXT.account,
			'api-key': API_KEY_CONTEXT.apiKey,
			'content-control': 'private'
		};
		if (localStorage.getItem('nocache')) {
			headers['cache-control'] = 'no-cache';
		}

		const response = await LiveApiClient.get(attribute_endpoint, {
			headers,
			signal: abortController.current?.signal
		});
		setAttributes(response.data);
		DB.Set(dbName, endpoint_parts, response.data);

		return response.data;
	}

	const setItemInUrl = React.useCallback((id = undefined) => {
		const url = new URL(window.location.href);
		if (id) {
			url.searchParams.set('item', id)
		} else {
			url.searchParams.delete('item');
			url.searchParams.delete('media-index');
		}
		window.history.pushState(null, null, url.toString());
	}, []);

	const getProduct = React.useCallback(async (productValue, throwOnError = undefined, isItemId = undefined) => {
		if (!productValue) return;

		try {
			const product = await getProductRequest(productValue, { isItemId });
			if (!product) {
				throw new Error(`Product ${productValue} not found`);
			}
			return product;
		} catch (e) {
			if (throwOnError) {
				throw e;
			} else if (!(e instanceof CanceledError)) {
				console.error("Failed to load product", e);
			}
		}
	}, [getProductRequest]);

	React.useEffect(() => {
		abortController.current?.abort();
		abortController.current = new AbortController();

		doPreload(product);

		preloadNextVariations(product, abortController.current?.signal);
	}, [product]);

	const preloadNextVariations = React.useCallback(async (product, signal) => {
		if (!product?.variations) {
			return;
		}

		const closestVariationItemIds = Object.values(product?.variations).reduce((acc, it) => {
			const set = acc ?? new Set();
			it.forEach(([_, id, dist]) => {
				if (+dist === 1) {
					set.add(+id);
				}
			});
			return set;
		}, null);
		if (closestVariationItemIds?.size) {
			const preloadChunks = closestVariationItemIds
				? ArrayUtil.Collate(Array.from(closestVariationItemIds), 5)
				: [];

			setLoadingNextVariations(!!preloadChunks.length);

			for (let itemIds of preloadChunks) {
				if (signal?.aborted) {
					break;
				}
				await Promise.allSettled(itemIds.map(async itemId => {
					const preloadedProduct = await ProductUtil.GetPreloadedProduct(catalog.id, ["id", itemId]);
					if (!preloadedProduct?.variations) {
						const product = await ProductUtil.GetProduct(itemId, {
							variations: true,
							signal,
							apiContext: API_KEY_CONTEXT,
						});
						if (product) {
							await ProductUtil.SetPreloadedProduct(catalog.id, cacheKeyPrefixes, product);
						}
					}
				}))
			}
		}
		setLoadingNextVariations(false);
	}, []);

	const doPreload = () => {
		const groupField = product?.grouping;
		const groupCode = product?.[groupField];
		if (!groupCode) {
			return;
		}

		const options = { ...common_request_headers.current };
		options.abortController = new AbortController();

		ProductUtil.PreloadProductGroup({
			catalog,
			groupField,
			groupCode,
			attributes: attributes || null,
			loadOptions: options,
		});
	};

	React.useEffect(() => {
		setLoading(true);

		getAttributes()
			.then(async () => {
				setModalProps(current => ({ ...current, product_not_found: undefined }));
				const thisProduct = await getProduct(modalProps.item);

				setProduct(thisProduct);
				loaded_item.current = thisProduct ? modalProps.item : undefined;

				if (modalProps.item && !thisProduct) {
					setModalProps(current => ({ ...current, product_not_found: true }));
					setItemInUrl(null);
					no_product_modal?.current?.showModal();
					setTimeout(() => no_product_modal?.current?.close(), NO_PRODUCT_FOUND_AUTO_CLOSE_TIMEOUT_MILLIS);
				} else if (!Catalog.IsPublic(catalog)) {
					setItemInUrl(thisProduct?.id);
				}
			})
			.finally(() => {
				setLoading(false);
			});
	}, [
		modalProps.item,
		API_KEY_CONTEXT,
		catalog?.options?.type,
		catalog.detail_fieldset?.handle,
	]);

	const media = product?.media?.reduce((r, a) => {
		r[a.type] ??= [];
		r[a.type].push(a);
		return r;
	}, Object.create(null));

	const downloadMedia = () => {
		Object.values(media).forEach(mediaByType =>
			mediaByType.forEach((m, index) =>
				preloaded_timeouts.current.add(setTimeout(() => download(processImagePath(m.source)), index * 500))
			)
		);
	};

	const changeItem = id => {
		cancelPreloads();
		setModalProps({ ...modalProps, item: id });
	}

	const showModal = React.useCallback(() => {
		actionBus?.send("load");
	}, []);

	const closeModal = React.useCallback(() => {
		cancelPreloads();
		setModalProps({ ...modalProps, item: null });
	}, [modalProps, setModalProps]);
	window.addEventListener("popstate", () => modalProps.item && setModalProps(null));


	const close_no_product_dialog = React.useCallback(() => {
		no_product_modal.current.close();
	}, []);

	const copyToClipboard = (text) => {
		navigator.clipboard.writeText(text);

		Dialog.fire({
			showConfirmButton: false,
			text: `Successfully copied to clipboard!`,
			icon: "success",
			confirmButtonText: "Ok",
			timer: 1500,
		})
	};


	/**********************************************  ********************************************************/
	/********************************* HOOKS MUST BE ABOVE THIS LINE *****************************************/
	/**********************************************  ********************************************************/

	if (!attributes || !product) {
		actionBus?.send("load");
		return (
			<>
				<dialog id="no-product-found-dialog" ref={no_product_modal} onClick={close_no_product_dialog}>
					<div className="banner">Product Not Found</div>
				</dialog>
				{empty_loading_container}
			</>
		);
	}

	trackEvent("Item", "view", product.common_sku);

	// const startChat = () => {
	// 	setChatTarget(new Item(product));
	// 	const message = new ChatMessage({
	// 		target_class: "Item",
	// 		target_id: modalProps.item,
	// 	});
	//
	// 	addNewChatMessage(message);
	// 	setToggleSidebar(true);
	// 	setSidebarContent("chat");
	// };

	const access = catalog.options?.proposals?.access && currentUser;
	const addButtonAccess = access && (access !== "view")
		&& (product?.retail_price || product["data:sell_price"]);

	// downloadEnabled is initially true
	// but by deleting modalProps, modalProps is updated so it's rerendered
	// and now downloadEnabled is false
	const downloadEnabled = modalProps.enableDownload;
	delete modalProps.enableDownload;
	const variations = product?.variations || {};

	let paramed_url_to_html;
	let org_name;
	let qr_url;
	const organization_member_data = JSON.parse(localStorage.getItem('organization_member:data')) || organizationMember;
	const organization = organization_member_data?.organization || false;
	const bookmarksEnabled = organization?.features?.catalog?.lists?.bookmarks !== false;


	if (product.html) {
		const url_options = {
			product,
			profile_token: organization_member_data?.profile?.public_token,
			organization: organization_member_data?.organization?.handle,
			catalog: catalog?.slug,
		};
		paramed_url_to_html = ProductUtil.Make_Html_Url(url_options);
		qr_url = ProductUtil.Make_Html_Url(url_options);
		org_name = organization?.name;
	}

	const on_qr_icon_click = e => {
		const target = e.target.parentNode;
		// add padding to media so image shift downward (cuz it's centered)
		const modal_content = target.closest(".modal-content");
		const footer_height = modal_content.querySelector(".modal-footer").getBoundingClientRect().height;
		modal_content.style.setProperty("--open-qr-modal-media-balance-padding", footer_height);
		setShowQr(true);
	}

	const on_modal_escape = e => {
		// if qr code open, only close that; not the whole detail
		if (showQr) {
			e.preventDefault();
			setShowQr(false);
		}
	}

	if (!product.media?.length && product.image_0) {
		product.media = [{
			source: product.image_0,
		}];
	}

	const catalogProductOptions = catalog.options?.product_attribute;
	const catalogAttributeInvariationOptions = catalogProductOptions?.invariation;
	// allow page-level overrides of catalog-level invariation configs
	const pageInvariationOptions = currentPage?.options?.product_attribute?.invariation ?? {};

	// merge the options together, allowing page-level options to override catalog-level ones
	const productAttributeInvariationOptions = ObjectUtil.DeepMerge({}, catalogAttributeInvariationOptions, pageInvariationOptions);
	// all options need to account for not being set. so for some undefined is not the same as false; double equal  null to account for null or undefined
	const allowInvariantVariationChange = productAttributeInvariationOptions?.allow_change == null || productAttributeInvariationOptions?.allow_change;
	const allowInvariantVariationContinueChange = productAttributeInvariationOptions?.allow_continue_change;
	const allowInvariantVariationMessage = productAttributeInvariationOptions?.warning?.message;
	const invariationOptions = {
		allow_change: allowInvariantVariationChange,
		allow_continue_change: allowInvariantVariationContinueChange,
		warning: {
			message: allowInvariantVariationMessage,
		},
	};
	const loadRelatedItems = items => {
		setRelatedItemCount(Object.values(items).reduce((agg, cur) => agg += cur.length, 0));
	};
	const invariationBus = new ActionBus()
		.on(ProductInvariationDialog.Actions.ACCEPT_INVARIATION_CHANGE, variation => changeItem(variation));

	const attributesBus = new ActionBus()
		.on(ProductInvariationDialog.Actions.INVARIATION_CHANGE, variation => {
			invariationBus.send(ProductInvariationDialog.Actions.SHOW, variation);
		});

	const VariationUnavailableModal = () => variations && Object.keys(variations).length && (
		<ProductInvariationDialog
			allowChange={allowInvariantVariationChange}
			allowContinue={allowInvariantVariationContinueChange}
			warningMessage={allowInvariantVariationMessage}
			actionBus={invariationBus}
		/>
	);
	const searchParams = new URLSearchParams((new URL(window.location)).search);
	const layout = searchParams.get('layout') || currentPage.options?.layout?.details;

	const ProductActionsShare = ({ product }) => {
		if (catalog?.options?.type !== "public") {
			const shareOptions = [
				{
					id: 'copy',
					label: 'Copy Link to Clipboard',
					action: () => copyToClipboard(paramed_url_to_html)
				}, {
					id: 'qr',
					label: 'View QR Code',
					action: on_qr_icon_click
				}, {
					id: 'sms',
					label: 'Share via Text',
					action: () => setShowPhoneNumberPrompt(true)
				}, {
					id: 'email',
					label: 'Share via Email',
					action: () => setShowEmailPrompt(true)
				}, {
					id: 'share',
					label: 'Share Link',
					action: () => navigator.share({
						url: paramed_url_to_html,
						title: (org_name ? org_name + ': ' : '') + (product.name || product.common_sku),
						description: product.description
					}).then(() => console.log('Shared')).catch(console.error)
				}, {
					id: 'new-window',
					label: 'Open In New Window',
					action: () => window.open(paramed_url_to_html)
				}
			];
			const shareFeatures = organization?.features?.catalog?.pdp;
			const featuresEnabled = shareOptions.filter(option => shareFeatures?.[option.id] !== false);
			const shareButtons = featuresEnabled.map(option => (
				<OverlayTrigger key={option.id} overlay={<Tooltip className="_boss tooltip-info">{option.label}</Tooltip>}>
					<button className={option.id} key={option.id} aria-label={option.label} onClick={option.action} />
				</OverlayTrigger>
			));

			return (
				<div className="share">
					{product.html && (
						<>
							{shareButtons}
							<div className="qr-code-wrapper">
								{showQr && (<ScannableCodeFullscreen url={qr_url.toString()} width={250} onClose={() => setShowQr(false)} />)}
							</div>
							{showPhoneNumberPrompt && (
								<ProductShareViaSmsModal
									phoneNumber={customerPhone}
									onClose={() => setShowPhoneNumberPrompt(false)}
									items={[product]}
								/>
							)}

							{showEmailPrompt && (
								<ProductShareViaEmailModal
									emails={customerEmail ? [customerEmail] : undefined}
									onClose={() => setShowEmailPrompt(false)}
									items={[product]}
									subject={product.name || product.name || product.common_sku}
								/>
							)}

						</>
					)}
				</div>
			);
		}
	}



	const container = (
		<div
			className={[
				"product-detail",
				loading && "is-loading",
				product.stone?.certificate && "is-stone",
				relatedItemCount && "has-related-items"
			].filter(v => v).join(" ")}
			data-sku={product?.common_sku}
		>
			<div className="actions">
				<ProductDetailMenu item={product} catalog={catalog} close={closeModal} />
			</div>
			<div className="main">
				<div className={"media count-" + product.media?.length}>
					{product.media && (<ProductMedia media={product.media} key={product.id} />)}
				</div>
				<div className="product-attributes-wrapper" data-layout={layout}>
					<div id="product-info">
						{
							<ProductAttributes
								product={product}
								variations={variations}
								attributes={attributes}
								changeItem={changeItem}
								actionBus={attributesBus}
								invariationOptions={invariationOptions}
							/>
						}
						{
							product?.stone?.certificate && (
								<ProductAttribute attribute={{ reference: "certificate", label: "Certificate" }}
									data={product.stone.certificate} showLabel={true}
									key="certificate">
									{
										product.stone.certificate_url ? (
											<a id="certificate-link" href={product.stone.certificate_url} target="_blank"
												rel="noreferrer">
												{product.stone.certificate}
											</a>
										) : (
											<>{product.stone.certificate}</>
										)
									}
								</ProductAttribute>
							)
						}
					</div>
					<div id="product-actions">
						<ProductActionsShare product={product} />

						{!!addButtonAccess && <ProposalItemActions mode="button" item={product}></ProposalItemActions>}

						{currentUser
							&& bookmarksEnabled
							&& (<BookmarkItemButton itemId={modalProps.item} />)}

						{customerAllowed && <RequestProductButton productId={product.id} />}

						{forms.map((form, key) => (
							<FormButton
								key={key}
								values={{ item: product.id }}
								form={form}
								label={form.label}
								variant={form.variant} />
						))}

						{/*{*/}
						{/*	currentUser && catalog?.options?.enable_chat*/}
						{/*	&& (*/}
						{/*		<Button type="button" className="chat" onClick={startChat}>*/}
						{/*			Have a Question for {catalog.options.branding.name}?*/}
						{/*		</Button>*/}
						{/*	)*/}
						{/*}*/}

						{
							catalog.options.item_inquiry?.enabled && product
							&& (<ItemInquiryButton form={catalog.options.item_inquiry.form} item={product} />)
						}
					</div>
				</div>
			</div>
			<React.Suspense fallback={<FontAwesomeIcon icon={faRotate} spin />}>
				<ItemRelationships itemId={modalProps.item} changeItem={changeItem} onLoad={loadRelatedItems} />
			</React.Suspense>
		</div>
	);

	const download_click = e => {
		e.stopPropagation();
		downloadMedia();
		LogContext.log({
			url: window.location.href,
			page: currentPage,
			action: "download",
			item: modalProps.item
		}, {
			limit: {
				type: "short",
				key: ["item", modalProps.item, "download"],
			}
		});
	}

	const detail_container = modalProps.item && (
		<>
			<VariationUnavailableModal />
			{(
				<Modal
					{...modalProps}
					show={modalProps.item}
					className={'_boss _boss-offcanvas product-detail-modal'}
					id='_boss-modal'
					size="lg"
					aria-labelledby="contained-modal-title-vcenter"
					centered
					backdrop={false}
					enforceFocus={false}
					onEscapeKeyDown={on_modal_escape}
					css={SidebarContext.isOpen}
					onHide={closeModal}
					onShow={showModal}
				>
					<Modal.Body className="show-grid">
						{loading ? empty_loading_container : container}
					</Modal.Body>
					{
						<Modal.Footer>
							<div id="close-container">
								<div className={["circle-pulse-loader", loadingNextVariations && "active"].filter(it => it).join(" ")}></div>

								<Button onClick={closeModal}>Close</Button>
							</div>

							{!!addButtonAccess && <ProposalItemActions item={product}></ProposalItemActions>}

							{downloadEnabled
								&&
								(
									<OverlayTrigger
										overlay={<Tooltip className="_boss tooltip-info">Download media</Tooltip>}>
										<button
											aria-label="Download"
											className="download"
											onClick={download_click}
										/>
									</OverlayTrigger>
								)
							}
						</Modal.Footer>
					}
				</Modal>
			)}
		</>
	);

	return detail_container;
}
